import { useRef, useState } from 'react';
import { ArrowCounterClockwise, Pause, Play } from '@phosphor-icons/react';
import { match } from 'ts-pattern';
import { cn } from '@/lib/utils';

export interface PhoneVideoProps {
  /** Chegou ao fim. */
  onEnded: () => void;
  /**
   * Começou a tocar. `replay` quando era "Assistir de novo"; `resume` quando
   * estava pausado — o primeiro toque não tem nenhum dos dois.
   */
  onStarted: (how: { replay: boolean; resume: boolean }) => void;
  /** Pausou, com o progresso (0–100) de onde parou. */
  onPaused: (progressPct: number) => void;
  /** A cada avanço do vídeo, o progresso (0–100). Para medir desistência. */
  onProgress: (progressPct: number) => void;
}

/**
 * `#t=0.001` faz o iOS pintar o primeiro quadro em vez de uma caixa preta:
 * sem um `poster`, o Safari só mostra algo depois que a reprodução começa.
 */
const SRC = `${
  import.meta.env.VITE_WELCOME_VIDEO_URL || '/videos/welcome.mp4'
}#t=0.001`;

type Playback = 'idle' | 'playing' | 'paused' | 'ended';

const pct = (video: HTMLVideoElement) =>
  video.duration > 0
    ? Math.round((video.currentTime / video.duration) * 100)
    : 0;

/**
 * O vídeo de apresentação, num quadro de celular. Serve o wizard de
 * boas-vindas e o "Veja como funciona" do cabeçalho.
 *
 * O `<video>` vem SEM os controles nativos: eles trazem a barra de busca, e no
 * wizard pular para o final contaria como assistir. O único controle é o botão
 * de tocar/pausar sobre o quadro (a tecla de espaço e o Enter também funcionam
 * nele), e o `ended` do próprio elemento é o que avisa o fim — não um
 * cronômetro.
 *
 * O quadro é 9:16 com altura limitada pela tela, porque o vídeo é vertical
 * (gravado no celular) e o diálogo é mais largo que alto no desktop.
 *
 * Os eventos saem daqui pelos callbacks e quem usa é que manda ao PostHog:
 * só ele sabe de onde o vídeo foi aberto.
 */
export function PhoneVideo({
  onEnded,
  onStarted,
  onPaused,
  onProgress,
}: PhoneVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playback, setPlayback] = useState<Playback>('idle');
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused || video.ended) {
      const replay = video.ended;
      if (replay) video.currentTime = 0;
      onStarted({ replay, resume: !replay && playback === 'paused' });
      void video.play();
    } else {
      video.pause();
    }
  };

  const { Icon, label } = match(playback)
    .with('playing', () => ({ Icon: Pause, label: 'Pausar o vídeo' }))
    .with('paused', () => ({ Icon: Play, label: 'Continuar o vídeo' }))
    .with('ended', () => ({
      Icon: ArrowCounterClockwise,
      label: 'Assistir de novo',
    }))
    .with('idle', () => ({ Icon: Play, label: 'Assistir ao vídeo' }))
    .exhaustive();

  return (
    /* A altura manda e a largura vem do 9:16: com largura fixa e altura
       limitada, o quadro perdia a proporção de celular. */
    <div className="relative mx-auto aspect-[9/16] h-[min(55dvh,34rem)] max-w-full overflow-hidden rounded-[1.75rem] border-[6px] border-foreground bg-foreground shadow-lg">
      <video
        ref={videoRef}
        src={SRC}
        playsInline
        preload="metadata"
        controls={false}
        disablePictureInPicture
        onPlay={() => setPlayback('playing')}
        onPause={(event) => {
          // O `ended` também dispara `pause`; esse não é uma pausa da pessoa.
          if (event.currentTarget.ended) return;
          setPlayback('paused');
          onPaused(pct(event.currentTarget));
        }}
        onEnded={() => {
          setPlayback('ended');
          onEnded();
        }}
        onTimeUpdate={(event) => {
          const { currentTime, duration } = event.currentTarget;
          if (duration > 0) {
            setProgress(currentTime / duration);
            onProgress(pct(event.currentTarget));
          }
        }}
        className="size-full object-cover"
      >
        <track kind="captions" />
      </video>

      {/* Sobre o vídeo inteiro: a área de toque é o quadro, não só o ícone.
          O ícone some enquanto toca para não cobrir a cena — o botão continua
          lá, com o rótulo, para o teclado e o leitor de tela. */}
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        className="group absolute inset-0 grid place-items-center focus-visible:outline-none"
      >
        <span
          className={cn(
            'grid size-16 place-items-center rounded-full bg-background/90 text-foreground shadow-md transition-opacity group-focus-visible:ring-4 group-focus-visible:ring-primary',
            playback === 'playing' && 'opacity-0 group-hover:opacity-100'
          )}
        >
          <Icon size={32} weight="fill" aria-hidden />
        </span>
      </button>

      {/* Progresso só para ver — sem busca, de propósito. */}
      <div
        role="progressbar"
        aria-label="Progresso do vídeo"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        className="absolute inset-x-4 bottom-3 h-1 overflow-hidden rounded-full bg-background/40"
      >
        <div
          className="h-full origin-left bg-background transition-transform duration-300 ease-linear"
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>
    </div>
  );
}
